import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material'
import {
  Print,
  Check,
  Assignment,
  FilterList,
  Clear,
} from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions, ExportButtons } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const columns = [
  { field: 'job_card_number', headerName: 'Job Card #', sortable: true, width: 130 },
  { field: 'work_order_number', headerName: 'Work Order #', sortable: true, width: 130 },
  { field: 'work_order_title', headerName: 'Title', sortable: true },
  { field: 'asset_name', headerName: 'Asset', sortable: true },
  { field: 'asset_tag', headerName: 'Tag', sortable: true, width: 100 },
  {
    field: 'generated_date',
    headerName: 'Generated',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
  { field: 'generated_by_name', headerName: 'Generated By', sortable: true },
  {
    field: 'user_acknowledged',
    headerName: 'User',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value ? 'ACKNOWLEDGED' : 'PENDING'}
        color={value ? 'success' : 'warning'}
        size="small"
      />
    ),
  },
  {
    field: 'is_acknowledged',
    headerName: 'Supervisor',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value ? 'ACKNOWLEDGED' : 'PENDING'}
        color={value ? 'success' : 'warning'}
        size="small"
      />
    ),
  },
]

const JobCardsList = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [jobCards, setJobCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedJobCard, setSelectedJobCard] = useState(null)
  const [showFilters, setShowFilters] = useState(false)

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  // Supervisor acknowledge dialog
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false)
  const [acknowledgmentNotes, setAcknowledgmentNotes] = useState('')
  const [acknowledgeLoading, setAcknowledgeLoading] = useState(false)

  // User acknowledge dialog
  const [userAckOpen, setUserAckOpen] = useState(false)
  const [userAckNotes, setUserAckNotes] = useState('')
  const [userAckLoading, setUserAckLoading] = useState(false)

  // Filter states
  const [filters, setFilters] = useState({
    is_acknowledged: '',
  })

  // Refs for stable callbacks
  const filtersRef = useRef(filters)
  const searchTermRef = useRef(searchTerm)
  const pageSizeRef = useRef(pageSize)

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'job_card_number', headerName: 'Job Card #' },
    { field: 'work_order_number', headerName: 'Work Order #' },
    { field: 'work_order_title', headerName: 'Title' },
    { field: 'asset_name', headerName: 'Asset' },
    { field: 'asset_tag', headerName: 'Tag' },
    { field: 'generated_date', headerName: 'Generated', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'generated_by_name', headerName: 'Generated By' },
    { field: 'user_acknowledged', headerName: 'User Status', renderValue: (v) => v ? 'ACKNOWLEDGED' : 'PENDING' },
    { field: 'user_acknowledged_by_name', headerName: 'User Ack By' },
    { field: 'is_acknowledged', headerName: 'Supervisor Status', renderValue: (v) => v ? 'ACKNOWLEDGED' : 'PENDING' },
    { field: 'acknowledged_by_name', headerName: 'Supervisor Ack By' },
  ], [])

  // Keep refs in sync
  useEffect(() => { filtersRef.current = filters }, [filters])
  useEffect(() => { searchTermRef.current = searchTerm }, [searchTerm])
  useEffect(() => { pageSizeRef.current = pageSize }, [pageSize])

  const fetchJobCards = useCallback(async (currentPage, currentPageSize, search) => {
    try {
      setLoading(true)
      const currentFilters = filtersRef.current
      const params = {
        page: currentPage + 1,
        page_size: currentPageSize,
      }
      if (search) params.search = search
      if (currentFilters.is_acknowledged !== '') params.is_acknowledged = currentFilters.is_acknowledged

      const response = await api.get('/maintenance/job-cards/', { params })
      setJobCards(response.data.results || response.data)
      setTotalCount(response.data.count || 0)
    } catch (error) {
      console.error('Error fetching job cards:', error)
      toast.error('Failed to load job cards')
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePaginationChange = useCallback(({ page: newPage, pageSize: newPageSize }) => {
    setPage(newPage)
    setPageSize(newPageSize)
    fetchJobCards(newPage, newPageSize, searchTermRef.current)
  }, [fetchJobCards])

  const handleSearch = useCallback((search) => {
    setSearchTerm(search)
    setPage(0)
    fetchJobCards(0, pageSizeRef.current, search)
  }, [fetchJobCards])

  // Initial load and when filters change
  useEffect(() => {
    setPage(0)
    setSearchTerm('')
    fetchJobCards(0, pageSizeRef.current, '')
  }, [filters, fetchJobCards])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ is_acknowledged: '' })
  }

  const hasActiveFilters = filters.is_acknowledged !== ''

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.is_acknowledged !== '') labels.Status = filters.is_acknowledged === 'true' ? 'Acknowledged' : 'Pending'
    return labels
  }

  const handleRowClick = async (row) => {
    try {
      // Fetch full details
      const response = await api.get(`/maintenance/job-cards/${row.id}/`)
      setSelectedJobCard(response.data)
      setDetailOpen(true)
    } catch (error) {
      console.error('Error fetching job card details:', error)
      toast.error('Failed to load job card details')
    }
  }

  const handleAcknowledge = async () => {
    setAcknowledgeLoading(true)
    try {
      await api.post(`/maintenance/job-cards/${selectedJobCard.id}/acknowledge/`, {
        notes: acknowledgmentNotes,
      })
      toast.success('Job card acknowledged successfully')
      fetchJobCards(page, pageSize, searchTerm)
      setDetailOpen(false)
      setAcknowledgeOpen(false)
      setAcknowledgmentNotes('')
    } catch (error) {
      console.error('Error acknowledging job card:', error)
      toast.error(error.response?.data?.error || 'Failed to acknowledge job card')
    } finally {
      setAcknowledgeLoading(false)
    }
  }

  const handleUserAcknowledge = async () => {
    setUserAckLoading(true)
    try {
      await api.post(`/maintenance/job-cards/${selectedJobCard.id}/user_acknowledge/`, {
        notes: userAckNotes,
      })
      toast.success('Work completion acknowledged')
      fetchJobCards(page, pageSize, searchTerm)
      setDetailOpen(false)
      setUserAckOpen(false)
      setUserAckNotes('')
    } catch (error) {
      console.error('Error acknowledging job card (user):', error)
      toast.error(error.response?.data?.error || 'Failed to acknowledge work completion')
    } finally {
      setUserAckLoading(false)
    }
  }

  const handlePrint = () => {
    navigate(`/maintenance/job-cards/${selectedJobCard.id}/print`)
  }

  return (
    <Box>
      <PageHeader
        title="Job Cards"
        subtitle="View and manage maintenance job cards"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Maintenance', path: '/maintenance' },
          { label: 'Job Cards' },
        ]}
        actions={
          <>
            <ExportButtons
              data={jobCards}
              columns={exportColumns}
              title="Job Cards List"
              filename="job_cards"
              filters={getActiveFilterLabels()}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={hasActiveFilters ? 'primary' : 'inherit'}
            >
              Filters {hasActiveFilters && `(1)`}
            </Button>
          </>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.is_acknowledged}
                onChange={handleFilterChange('is_acknowledged')}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Acknowledged</MenuItem>
                <MenuItem value="false">Pending</MenuItem>
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={jobCards}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search job cards..."
          emptyMessage="No job cards found"
          serverSide
          totalCount={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPaginationChange={handlePaginationChange}
          onSearch={handleSearch}
        />
      </Card>

      {/* Job Card Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment />
            Job Card {selectedJobCard?.job_card_number}
            {selectedJobCard && (
              <Chip
                label={selectedJobCard.user_acknowledged ? 'USER ACKNOWLEDGED' : 'USER PENDING'}
                color={selectedJobCard.user_acknowledged ? 'success' : 'warning'}
                size="small"
                sx={{ ml: 2 }}
              />
            )}
            {selectedJobCard && (
              <Chip
                label={selectedJobCard.is_acknowledged ? 'SUPERVISOR ACKNOWLEDGED' : 'SUPERVISOR PENDING'}
                color={selectedJobCard.is_acknowledged ? 'success' : 'warning'}
                size="small"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedJobCard && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Work Order Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary">Work Order Information</Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Work Order #</Typography>
                <Typography>{selectedJobCard.work_order_number}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Title</Typography>
                <Typography>{selectedJobCard.work_order_title}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Typography>{selectedJobCard.work_order_category || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Priority</Typography>
                <Typography>{selectedJobCard.work_order_priority || 'N/A'}</Typography>
              </Grid>
              {selectedJobCard.work_order_description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography>{selectedJobCard.work_order_description}</Typography>
                </Grid>
              )}

              {/* Asset Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Asset Information</Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Asset Name</Typography>
                <Typography>{selectedJobCard.asset_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Asset Tag</Typography>
                <Typography>{selectedJobCard.asset_tag || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                <Typography>{selectedJobCard.asset_serial_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography>{selectedJobCard.asset_location || 'N/A'}</Typography>
              </Grid>

              {/* Work Details */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Work Details</Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                <Typography>{selectedJobCard.assigned_to_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Section/Unit</Typography>
                <Typography>{selectedJobCard.department_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Start Time</Typography>
                <Typography>{selectedJobCard.start_time ? formatDateTime(selectedJobCard.start_time) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Completion Time</Typography>
                <Typography>{selectedJobCard.completion_time ? formatDateTime(selectedJobCard.completion_time) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Actual Hours</Typography>
                <Typography>{selectedJobCard.actual_hours || 'N/A'}</Typography>
              </Grid>
              {selectedJobCard.problem_description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Problem Description</Typography>
                  <Typography>{selectedJobCard.problem_description}</Typography>
                </Grid>
              )}
              {selectedJobCard.resolution_description && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Resolution</Typography>
                  <Typography>{selectedJobCard.resolution_description}</Typography>
                </Grid>
              )}

              {/* Parts Used */}
              {selectedJobCard.parts_used && selectedJobCard.parts_used.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Parts Used</Typography>
                    <Divider sx={{ mb: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Part</TableCell>
                            <TableCell>Part #</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Unit Cost</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedJobCard.parts_used.map((part, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{part.part_name || part.spare_part_name}</TableCell>
                              <TableCell>{part.part_number || '-'}</TableCell>
                              <TableCell align="right">{part.quantity}</TableCell>
                              <TableCell align="right">{part.unit_cost?.toLocaleString() || '-'}</TableCell>
                              <TableCell align="right">{part.total_cost?.toLocaleString() || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </>
              )}

              {/* Labor Records */}
              {selectedJobCard.labor_records && selectedJobCard.labor_records.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Labor Records</Typography>
                    <Divider sx={{ mb: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Technician</TableCell>
                            <TableCell>Task</TableCell>
                            <TableCell align="right">Hours</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedJobCard.labor_records.map((labor, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{labor.technician_name}</TableCell>
                              <TableCell>{labor.task_description || '-'}</TableCell>
                              <TableCell align="right">{labor.hours_worked}</TableCell>
                              <TableCell align="right">{labor.hourly_rate?.toLocaleString() || '-'}</TableCell>
                              <TableCell align="right">{labor.total_cost?.toLocaleString() || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </>
              )}

              {/* Job Card Info */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Job Card Information</Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Generated Date</Typography>
                <Typography>{selectedJobCard.generated_date ? formatDateTime(selectedJobCard.generated_date) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Generated By</Typography>
                <Typography>{selectedJobCard.generated_by_name || 'N/A'}</Typography>
              </Grid>

              {/* User/Requester Acknowledgement (always visible) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>User Acknowledgement</Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={selectedJobCard.user_acknowledged ? 'ACKNOWLEDGED' : 'PENDING'}
                    color={selectedJobCard.user_acknowledged ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Acknowledged By</Typography>
                <Typography>{selectedJobCard.user_acknowledged_by_name || 'Not yet acknowledged'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Acknowledged At</Typography>
                <Typography>{selectedJobCard.user_acknowledged_at ? formatDateTime(selectedJobCard.user_acknowledged_at) : 'N/A'}</Typography>
              </Grid>
              {selectedJobCard.user_acknowledgment_notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">User Notes</Typography>
                  <Typography>{selectedJobCard.user_acknowledgment_notes}</Typography>
                </Grid>
              )}

              {/* Supervisor Acknowledgement (always visible) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>Supervisor Acknowledgement</Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={selectedJobCard.is_acknowledged ? 'ACKNOWLEDGED' : 'PENDING'}
                    color={selectedJobCard.is_acknowledged ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Acknowledged By</Typography>
                <Typography>{selectedJobCard.acknowledged_by_name || 'Not yet acknowledged'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Acknowledged At</Typography>
                <Typography>{selectedJobCard.acknowledged_at ? formatDateTime(selectedJobCard.acknowledged_at) : 'N/A'}</Typography>
              </Grid>
              {selectedJobCard.acknowledgment_notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Acknowledgment Notes</Typography>
                  <Typography>{selectedJobCard.acknowledgment_notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          <Button
            startIcon={<Print />}
            variant="outlined"
            onClick={handlePrint}
          >
            Print
          </Button>
          {selectedJobCard && !selectedJobCard.user_acknowledged && (
            <PermissionGate permission="job_cards.user_acknowledge">
              <Button
                startIcon={<Check />}
                color="success"
                onClick={() => setUserAckOpen(true)}
              >
                User Acknowledge
              </Button>
            </PermissionGate>
          )}
          {selectedJobCard && !selectedJobCard.is_acknowledged && (
            <PermissionGate permission="job_cards.acknowledge">
              <Button
                startIcon={<Check />}
                color="success"
                onClick={() => setAcknowledgeOpen(true)}
              >
                Supervisor Acknowledge
              </Button>
            </PermissionGate>
          )}
        </DialogActions>
      </Dialog>

      {/* Acknowledge Dialog */}
      <Dialog open={acknowledgeOpen} onClose={() => setAcknowledgeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Acknowledge Job Card</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By acknowledging this job card, you confirm that the maintenance work has been completed satisfactorily.
          </Typography>
          <TextField
            fullWidth
            label="Acknowledgment Notes (Optional)"
            value={acknowledgmentNotes}
            onChange={(e) => setAcknowledgmentNotes(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setAcknowledgeOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAcknowledge}
            loading={acknowledgeLoading}
            color="success"
          >
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Acknowledge Dialog */}
      <Dialog open={userAckOpen} onClose={() => setUserAckOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Acknowledge Resolution</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            By acknowledging, you confirm that the issue described in this job card has been resolved.
          </Typography>
          <TextField
            fullWidth
            label="Notes (Optional)"
            value={userAckNotes}
            onChange={(e) => setUserAckNotes(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setUserAckOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUserAcknowledge}
            loading={userAckLoading}
            color="success"
          >
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default JobCardsList
